import { ArrowRight, CheckCircle2, FileText } from "lucide-react"
import { useEditorStore } from "@/features/page-editor/store/editor-store"
import { useStudioStore } from "@/store/studio-store"
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button"
import { useStudioProjectOutputKind } from "../components/artifact-mode-switcher"
import { activateStudioAuthorityTask } from "../studio-authority-activation"
import { useStudioLocale } from "@/i18n"
import { getRailSurfaceCopy } from "./rail-surface-copy"
import { BlockOntologyPanel } from "@/features/design-system/block-view/block-ontology-panel"

/**
 * Map surface — IDE v2 inner shell for the Map rail item.
 *
 * Shows a flat list of pages in the current project so users can see the
 * site map at a glance. Future: hierarchical tree, deep-link routing graph,
 * navigation overlay. For brand-new "Start from scratch" projects this
 * shows an empty state instead of the previous "rail item not yet wired"
 * placeholder.
 */
export function MapSurface() {
  const { locale } = useStudioLocale()
  const copy = getRailSurfaceCopy(locale).map
  const outputKind = useStudioProjectOutputKind()
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)
  const setStudioMode = useStudioStore((s) => s.setStudioMode)
  const setViewMode = useStudioStore((s) => s.setViewMode)
  const setIdeActiveTab = useStudioStore((s) => s.setIdeActiveTab)
  const pages = app?.pages ?? []

  const openPage = (pageId: string) => {
    useEditorStore.getState().setActivePage(pageId)
    setStudioMode("edit")
    setViewMode("editor")
    setIdeActiveTab("pages")
    activateStudioAuthorityTask({
      domain: "primary",
      outputKind,
      railItem: "editor",
    })
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                {copy.eyebrow}
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {copy.title}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {copy.description}
              </p>
            </div>
            <AskCogoSurfaceButton
              label={copy.reviewSiteMap}
              chatContextPatch={{
                ...(activePageId ? { pageId: activePageId } : {}),
              }}
              context={() =>
                [
                  "Studio map surface: review site structure, page order, routes, and navigation changes.",
                  `Pages=${pages.length}`,
                  activePageId ? `Active page=${activePageId}` : null,
                ]
                  .filter(Boolean)
                  .join("; ")
              }
            />
          </div>
          {activePageId && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              {copy.activePage}
              <code className="font-mono text-foreground">{activePageId}</code>
            </div>
          )}
        </header>

        {pages.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
            <div className="mx-auto mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <FileText className="h-4 w-4" aria-hidden />
            </div>
            <div className="text-sm font-medium text-foreground">
              {copy.emptyTitle}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{copy.emptyBody}</p>
          </div>
        ) : (
          <ol className="space-y-2">
            {pages.map((p, i) => (
              <li
                key={p.id ?? `page-${i}`}
                className="rounded-md border border-border bg-card px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      {activePageId === p.id && (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      )}
                      <div className="truncate text-sm font-medium text-foreground">
                        {p.title ?? p.name ?? p.id ?? copy.untitledPage}
                      </div>
                    </div>
                    <div className="truncate font-mono text-[11px] text-muted-foreground">
                      {p.route ?? p.id ?? "/"}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                    {copy.blockCount(p.blocks?.length ?? 0)}
                  </span>
                  <button
                    type="button"
                    onClick={() => p.id && openPage(p.id)}
                    disabled={!p.id}
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={copy.openPage(
                      p.title ?? p.name ?? p.id ?? copy.untitledPage,
                    )}
                  >
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    <ArrowRight className="h-3 w-3" aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    {/* The other half of "what is my app made of": the map lists the pages,
        this says what each block does when something is pressed. Mounted here
        because this is where the project actually is — the design-system
        page has no open project, measured in a real browser. */}
    <BlockOntologyPanel />
    </div>
  )
}
